import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, addDoc, Timestamp, onSnapshot,where, query, orderBy } from "firebase/firestore";
import styled, { keyframes, createGlobalStyle, css } from "styled-components";
import { enqueueSnackbar } from "notistack";
import { auth,onAuthStateChanged  } from "../firebaseConfig";

// ─── Constants ────────────────────────────────────────────────────────────────

const SUPPLIERS = {
  HOTEL: [
    "Aden", "Al Multazam", "Akbar Travel", "Al Subee", "Al Sabir", "Anjum Hotel", "Arabian",
    "Asdaf", "Aston", "Rowa", "B2B Bookings", "Basma", "Daniyal", "Emaar UK",
    "Emerging Travel UK LTD", "EmtStays", "First Quest", "Fundaq",
    "Hassan Kamran Travel", "Heavan Travel & Tours", "Holiday Me", "Jhelum Inn",
    "Muhabiya", "Ratehawk", "Rezlive", "ROD", "Saja Al Madinah Hotel",
    "Stuba", "TBO", "Wirgan Aziziah", "Zaha Al Munawara",
  ],

  FLIGHT: [
    "Masterfare", "Brightsun", "Polani", "Air Arabia", "Air Asia", "Al Qemah",
    "British Airways", "EasyJet", "FlyNas", "Hassan Kamran Travel",
    "Heavan Travel & Tours", "National Air Service", "NO1LOUNGES", "Panama",
    "Pegasus", "Ryan Air","Citibond","AeroGlobe", "Akbar Travels","Sasta Ticket", "Visa Club Travel", "Wizz Air", "Profine Travel",
  ],

  VISA: [
    "Arabian", "Asdaf", "Asif Sahab", "AzeriCard", "Basma", "GM Travel",
    "Hassan Kamran Travel", "Heavan Travel & Tours", "Ibn e Mukhtar", "MJ Dreams",
    "Noor Taiba", "Profine Travel", "ROD Travel", "Turkey Visa Portal",
    "Visa Club", "Visit Saudi","Akbar Travels", "Yet Too Explore & Tours", "Nadra",
  ],

  TRANSPORT: [
    "Al Tawfeeq", "Arabian", "Basma", "GM Travel", "Hassan Kamran Travel",
    "HHR", "Ibn e Mukhtar", "Mozio", "Noor Taiba", "TBO", "Heavan Travel & Tours",
  ]
};

const getSupplierList = (type) => {
  switch (type) {
    case "Flight": return SUPPLIERS.FLIGHT;
    case "Hotel": return SUPPLIERS.HOTEL;
    case "Visa": return SUPPLIERS.VISA;
    case "Transport": return SUPPLIERS.TRANSPORT;
    default: return [];
  }
};

const TYPES = [
  { id: "Flight", icon: "✈", desc: "PNR-based ticket issuance", color: "#2563eb", bg: "#eff6ff" },
  { id: "Hotel", icon: "🏨", desc: "Booking ref & room details", color: "#0891b2", bg: "#ecfeff" },
  { id: "Transport", icon: "🚐", desc: "Transfer legs & routing", color: "#7c3aed", bg: "#f5f3ff" },
  { id: "Visa", icon: "🛂", desc: "Visa type & application", color: "#059669", bg: "#ecfdf5" },
];


const URGENCY = [
  { value: "Easy", label: "Standard", color: "#059669", bg: "#ecfdf5", dot: "#10b981" },
  { value: "High Alert", label: "High Alert", color: "#dc2626", bg: "#fef2f2", dot: "#ef4444" },
];



const getDisplayName = (email) => {
  if (!email) return "User";
  return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const emptyEntry = (type) => ({
  type, folderNo: "", ref: "", supplier: "", cost: "", urgency: "Easy", agent: "",
});

const getRefLabel = (type) => {
  const map = { Flight: "PNR", Hotel: "Booking Ref", Transport: "Legs", Visa: "Visa Type" };
  return map[type] || "Reference";
};

const getTypeConfig = (id) => TYPES.find((t) => t.id === id) || TYPES[0];

// ─── Global Styles ────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
`;

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'DM Sans', sans-serif;
  padding: 40px 24px 80px;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 40px;
`;

const PageLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.5px;
`;

const PageSub = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 32px 0;
`;

/* ── Type selection ── */

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (min-width: 600px) { grid-template-columns: repeat(4, 1fr); }
`;

const TypeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 20px;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s ease;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: ${({ $i }) => $i * 60}ms;

  &:hover {
    border-color: ${({ $color }) => $color};
    box-shadow: 0 4px 20px ${({ $color }) => $color}22;
    transform: translateY(-2px);
  }

  .icon {
    font-size: 26px;
    width: 44px; height: 44px;
    display: flex; align-items: center; justify-content: center;
    background: ${({ $bg }) => $bg};
    border-radius: 10px;
  }

  .type-name {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
  }

  .type-desc {
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.4;
  }
`;

/* ── Form section ── */

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  animation: ${fadeUp} 0.3s ease both;
`;

const FormTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .icon-badge {
    width: 40px; height: 40px;
    display: flex; align-items: center; justify-content: center;
    background: ${({ $bg }) => $bg};
    border-radius: 10px;
    font-size: 20px;
  }

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }

  span {
    font-size: 12px;
    color: #94a3b8;
  }
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 14px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f8fafc; color: #0f172a; border-color: #cbd5e1; }
`;

/* ── Entry card ── */

const EntryCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 12px;
  animation: ${slideIn} 0.25s ease both;
  position: relative;

  .card-num {
    position: absolute;
    top: -10px;
    left: 16px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1px;
    color: white;
    background: ${({ $color }) => $color};
    padding: 2px 10px;
    border-radius: 20px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  @media (min-width: 640px) { grid-template-columns: repeat(3, 1fr); }
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const Input = styled.input`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  transition: all 0.15s;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: ${({ $color }) => $color || "#2563eb"};
    background: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#2563eb"}18;
  }

  &::placeholder { color: #cbd5e1; }
`;

const StyledSelect = styled.select`
  padding: 9px 12px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 30px;

  &:focus {
    border-color: ${({ $color }) => $color || "#2563eb"};
    background-color: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#2563eb"}18;
  }
`;

/* ── Urgency toggle ── */

const UrgencyRow = styled.div`
  display: flex;
  gap: 8px;
`;

const UrgencyBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  border: 1.5px solid ${({ $active, $color }) => $active ? $color : "#e2e8f0"};
  background: ${({ $active, $bg }) => $active ? $bg : "#f8fafc"};
  color: ${({ $active, $color }) => $active ? $color : "#94a3b8"};
  transition: all 0.15s;

  .dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${({ $active, $dot }) => $active ? $dot : "#cbd5e1"};
    ${({ $active, $value }) => $active && $value === "High Alert" && css`animation: ${pulse} 1.2s ease infinite;`}
  }
`;

/* ── Action buttons ── */

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  border: 1.5px dashed ${({ $color }) => $color}55;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
  transition: all 0.15s;

  &:hover {
    border-style: solid;
    border-color: ${({ $color }) => $color};
    background: white;
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: ${({ $color }) => $color};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.2px;
  margin-top: 8px;

  &:hover { filter: brightness(1.07); transform: translateY(-1px); box-shadow: 0 6px 20px ${({ $color }) => $color}44; }
  &:active { transform: translateY(0); filter: brightness(0.97); }
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 26px; height: 26px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px;
  border: 1px solid #fee2e2;
  background: #fef2f2;
  color: #ef4444;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  transition: all 0.12s;
  &:hover { background: #fee2e2; }
`;

/* ── Requests table ── */

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
  }

  .count {
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    background: #f1f5f9;
    padding: 3px 10px;
    border-radius: 20px;
  }
`;

const RequestsTable = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 80px 90px 1fr 1fr 1fr 1fr 90px 100px;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  gap: 12px;

  span {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 80px 90px 1fr 1fr 1fr 1fr 90px 100px;
  padding: 12px 16px;
  border-bottom: 0.5px solid #f1f5f9;
  gap: 12px;
  align-items: center;
  transition: background 0.1s;
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${({ $i }) => Math.min($i * 30, 200)}ms;

  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }

  .cell {
    font-size: 13px;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .folder {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: #64748b;
  }
`;

const TypePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) => $status === "PENDING" ? "#fffbeb" : "#ecfdf5"};
  color:      ${({ $status }) => $status === "PENDING" ? "#92400e" : "#065f46"};

  &::before {
    content: '';
    width: 5px; height: 5px;
    border-radius: 50%;
    background: ${({ $status }) => $status === "PENDING" ? "#f59e0b" : "#10b981"};
    ${({ $status }) => $status === "PENDING" && css`animation: ${pulse} 1.4s ease infinite;`}
  }
`;

const UrgencyPill = styled.span`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $u }) => $u === "High Alert" ? "#fef2f2" : "#f0fdf4"};
  color:      ${({ $u }) => $u === "High Alert" ? "#dc2626" : "#16a34a"};
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;

  .icon { font-size: 32px; margin-bottom: 10px; opacity: 0.5; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function IssuanceRequestPage() {
  const [entries, setEntries] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const typeConfig = getTypeConfig(selectedType);


  const [user, setUser] = useState(null);

  const getDisplayName = (email) => {
  if (!email) return "User";
  return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, c => c.toUpperCase());
};


useEffect(() => {
  setUser(auth.currentUser);
}, []); 



useEffect(() => {
  const q = query(
    collection(db, "IssuanceRequest"),
    orderBy("createdAt", "desc")
  );

  const unsub = onSnapshot(q, (snap) => {
    const allData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // 👇 filter here on frontend
    const filtered = allData.filter(
      (item) => item.agent === getDisplayName(user?.email)
    );

    setRequests(filtered);
  });

  return () => unsub();
}, [user?.email]);



  const handleSelectType = (type) => { setSelectedType(type); setEntries([]); };

  const handleAddEntry = () => setEntries([...entries, emptyEntry(selectedType)]);

  const handleRemoveEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const handleChange = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
  };

  const validateEntries = () =>
    entries.every((e) => e.folderNo && e.ref && e.supplier && e.cost && e.ticketorder && e.urgency && getDisplayName(user?.email));

  const handleSubmit = async () => {

    if (entries.length === 0) return enqueueSnackbar("Add at least one entry", { variant: "warning" });
    if (!validateEntries()) return enqueueSnackbar("All fields are required", { variant: "error" });

    setSubmitting(true);
    try {
      for (const entry of entries) {
        await addDoc(collection(db, "IssuanceRequest"), {
          ...entry,
          agent:getDisplayName(user?.email),
          cost: parseFloat(entry.cost),
          status: "PENDING",
          createdAt: Timestamp.now(),
        });
      }
      enqueueSnackbar(`${entries.length} request${entries.length > 1 ? "s" : ""} submitted`, { variant: "success" });
      setEntries([]);
      setSelectedType("");
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error submitting requests", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };



  return (
    <>
      <GlobalStyle />
      <Page>
        <Inner>

          {/* ── Header ── */}
          <Header>
            <PageLabel>Operations</PageLabel>
            <PageTitle>Issuance Requests</PageTitle>
            <PageSub>Submit and track flight, hotel, transport & visa issuance requests</PageSub>
          </Header>

          {/* ── Type Selection ── */}
          {!selectedType && (
            <>
              <SectionTitle>
                <h3>Select request type</h3>
              </SectionTitle>
              <TypeGrid>
                {TYPES.map((t, i) => (
                  <TypeCard
                    key={t.id} $color={t.color} $bg={t.bg} $i={i}
                    onClick={() => handleSelectType(t.id)}
                  >
                    <div className="icon">{t.icon}</div>
                    <div>
                      <div className="type-name">{t.id}</div>
                      <div className="type-desc">{t.desc}</div>
                    </div>
                  </TypeCard>
                ))}
              </TypeGrid>
            </>
          )}

          {/* ── Entry Form ── */}
          {selectedType && (
            <>
              <FormHeader>
                <FormTitle $bg={typeConfig.bg}>
                  <div className="icon-badge">{typeConfig.icon}</div>
                  <div>
                    <h2>{typeConfig.id} Request</h2>
                    <span>{entries.length} entr{entries.length === 1 ? "y" : "ies"} added</span>
                  </div>
                </FormTitle>
                <BackBtn onClick={() => setSelectedType("")}>
                  ← Back
                </BackBtn>
              </FormHeader>

              {entries.map((entry, index) => (
                <EntryCard key={index} $color={typeConfig.color}>
                  <div className="card-num">#{index + 1}</div>
                  <RemoveBtn onClick={() => handleRemoveEntry(index)}>×</RemoveBtn>

                  <FieldGrid style={{ marginTop: 10 }}>
                    <FieldWrap>
                      <FieldLabel>Folder No</FieldLabel>
                      <Input
                        placeholder="e.g. 1042"
                        value={entry.folderNo}
                        onChange={(e) => handleChange(index, "folderNo", e.target.value)}
                        $color={typeConfig.color}
                      />
                    </FieldWrap>

                    <FieldWrap>
                      <FieldLabel>{getRefLabel(selectedType)}</FieldLabel>
                      <Input
                        placeholder={getRefLabel(selectedType)}
                        value={entry.ref}
                        onChange={(e) => handleChange(index, "ref", e.target.value)}
                        $color={typeConfig.color}
                      />
                    </FieldWrap>

                    <FieldWrap>
                      <FieldLabel>Agent</FieldLabel>
                      <Input
                        placeholder="Agent name"
                        value={getDisplayName(user?.email)}
                        $color={typeConfig.color}
                        readOnly
                      />
                    </FieldWrap>

                    <FieldWrap>
                      <FieldLabel>Supplier</FieldLabel>
                      <StyledSelect
                        value={entry.supplier}
                        onChange={(e) => handleChange(index, "supplier", e.target.value)}
                        $color={typeConfig.color}
                      >
                        <option value="">Select supplier</option>
                        {/* {SUPPLIERS.map((s) => <option key={s} value={s}>{s}</option>)} */}

                        {getSupplierList(entry.type).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </StyledSelect>
                    </FieldWrap>

                    <FieldWrap>
                      <FieldLabel>Cost (£)</FieldLabel>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={entry.cost}
                        onChange={(e) => handleChange(index, "cost", e.target.value)}
                        $color={typeConfig.color}
                      />
                    </FieldWrap>
                    <FieldWrap>
                      <FieldLabel>Urgency</FieldLabel>
                      <UrgencyRow>
                        {URGENCY.map((u) => (
                          <UrgencyBtn
                            key={u.value}
                            $active={entry.urgency === u.value}
                            $color={u.color} $bg={u.bg} $dot={u.dot} $value={u.value}
                            onClick={() => handleChange(index, "urgency", u.value)}
                          >
                            <span className="dot" />
                            {u.label}
                          </UrgencyBtn>
                        ))}
                      </UrgencyRow>
                    </FieldWrap>




                  </FieldGrid>


                  <FieldWrap>
                    <FieldLabel style={{ marginTop: 5 }}>Ticket Order</FieldLabel>
                    <textarea
                      placeholder=""
                      value={entry.ticketorder}
                      onChange={(e) => handleChange(index, "ticketorder", e.target.value)}
                      style={{
                        width: "100%",
                        minHeight: "250px",
                        marginTop: "10px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        resize: "vertical"
                      }}
                    />
                  </FieldWrap>
                </EntryCard>
              ))}

              <AddBtn
                $color={typeConfig.color} $bg={typeConfig.bg}
                onClick={handleAddEntry}
              >
                + Add another entry
              </AddBtn>

              {entries.length > 0 && (
                <SubmitBtn
                  $color={typeConfig.color}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Submitting…" : `Submit ${entries.length} Request${entries.length > 1 ? "s" : ""}`}
                </SubmitBtn>
              )}
            </>
          )}

          <Divider />

          {/* ── Submitted Requests ── */}
          <SectionTitle>
            <h3>Submitted Requests</h3>
            <span className="count">{requests.length} total</span>
          </SectionTitle>

          <RequestsTable>
            <TableHead>
              <span>Type</span>
              <span>Folder</span>
              <span>Reference</span>
              <span>Agent</span>
              <span>Supplier</span>
              <span>Cost</span>
              <span>Urgency</span>
              <span>Status</span>
            </TableHead>

            {requests.length === 0 && (
              <EmptyState>
                <div className="icon">📋</div>
                <div>No requests submitted yet</div>
              </EmptyState>
            )}

            {requests.map((req, i) => {
              const tc = getTypeConfig(req.type);
              return (
                <TableRow key={req.id} $i={i}>
                  <div className="cell">
                    <TypePill $color={tc.color} $bg={tc.bg}>
                      {tc.icon} {req.type}
                    </TypePill>
                  </div>
                  <div className="cell folder">#{req.folderNo}</div>
                  <div className="cell">{req.ref}</div>
                  <div className="cell">{req.agent}</div>
                  <div className="cell">{req.supplier}</div>
                   <div className="cell">£ {req.cost}</div>
                  <div className="cell">
                    <UrgencyPill $u={req.urgency}>{req.urgency}</UrgencyPill>
                  </div>
                  <div className="cell">
                    <StatusPill $status={req.status}>{req.status}</StatusPill>
                  </div>

                </TableRow>
              );
            })}
          </RequestsTable>

        </Inner>
      </Page>
    </>
  );
}